import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Layout } from 'src/common/components/Layout';
import { CourseHeader } from 'src/public/components/course-page/CourseHeader';
import styled, { css } from 'styled-components';
import { colors, shadows } from 'src/config/colors';
import courseSampleCertificate from 'src/assets/images/course-sample-certificate.png';
import { LayoutBox } from 'src/common/components/LayoutBox';
import { CourseEventSelectPanel } from 'src/public/components/course-page/CourseEventSelectPanel';
import { SectionTitle } from 'src/common/components/SectionTitle';
import { CourseFooter } from 'src/common/components/CourseFooter';
import { below, media, widthBreakpoints } from 'src/config/breakpoints';
import { MediaQuery } from 'src/common/migration/MediaQuery';
import { useDispatch } from 'react-redux';
import { LayoutPanelCategories } from 'src/common/components/Layout/LayoutPanelCategories';
import { CourseParameterList } from 'src/common/components/CourseParameterList';
import {
  CourseEvent,
  EventDTO,
  EventWithAvailabilityStatusDTO,
  mapToCourseEvent,
} from 'src/common/models/event';
import { courseSegmentsTranslations, getCourseEventSlugRoute } from 'src/course/routes';
import { CourseEventAgendaList } from 'src/course/components/CourseEventAgendaList';
import { CourseSpeaker } from 'src/public/components/course-page/CourseSpeaker';
import { layoutActions } from 'src/state/layout/layoutActions';
import { SimpleContentLoader } from 'src/common/components/SimpleContentLoader';
import { useResetRedirectUrl } from 'src/common/guards/useResetRedirectUrl';
import { isNilEmpty } from 'src/lib/isNilEmpty';
import { layers } from 'src/config/layers';
import { useGatsbyLikeNavigate } from 'src/common/migration/useGatsbyLikeNavigate';
import { RouteComponentProps } from 'src/common/migration/RouteComponentProps';
import { CourseRelatedCourses } from 'src/course/components/CourseRelatedCourses';
import { VendorGetServerSideProps } from 'vendor/utils/VendorGetServerSideProps';
import all from 'it-all';
import {
  autoselectEvent,
  getEventsByCourseId,
  listEventsOnlineGenerator,
} from 'src/common/services/event';
import { CourseDTO, mapToCourse } from 'src/common/models/course';
import { getCourseDTOById, getRelatedCourses } from 'src/common/services/course';
import { useCourseQuery } from 'src/common/hooks/course/useCourseQuery';
import { useCourseEventsQuery } from 'src/common/hooks/course/useCourseEventsQuery';
import { buildUnauthenticatedUserKeyEnhancer } from 'src/common/hooks/useCurrentUserKeyEnhancer';
import { QueryKey } from 'src/config/queryKey';
import { prepareKeywordsString } from 'src/common/services/seo';
import { useRouter } from 'next/router';
import { useRegisterAfterRedirect } from 'src/course/hooks/useRegisterAfterRedirect';
import { buildRedirectUrl, pageParam } from 'src/lib/router.utils';
import { HomepageRoute } from 'src/public/publicRoutes';
import { isErrorResponse } from 'src/common/models/error';
import { permanentRedirectCode, temporaryRedirectCode } from 'src/config/globals';
import { StatusCodes } from 'http-status-codes';
import { assertCourseWithEventParams } from 'src/common/ssr/course';
import { coerceInt } from 'src/lib/coerceInt';
import Image from 'next/image';

const CertificateImage = styled.div`
  flex: 0 0 auto;
  margin-right: 2.1875rem;

  > span {
    border-radius: 0.1875rem;
    box-shadow: ${shadows.drop2};
  }
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${media.w.min.px1200(css`
    display: grid;
    grid-template-columns: minmax(300px, 1fr) 13.625rem;
  `)};
`;

const Content = styled.div`
  order: 2;
  padding: 3.4375rem 0 0.9375rem;
  width: 100%;

  ${media.w.min.px768(css`
    order: 1;
    padding: 2.5rem 3.4375rem 2.5rem 1.25rem;
    width: 68%;
  `)};

  ${media.w.min.px992(css`
    padding: 1.5625rem 1.25rem 2.1875rem 0;
    width: 60%;
  `)};

  ${media.w.min.px1200(css`
    padding: 3.4375rem 2.5rem 4.0625rem 1.125rem;
    width: 100%;
  `)};
`;

const StyledCourseEventAgendaList = styled(CourseEventAgendaList)`
  margin-top: 0.8125rem;
`;

const RightPanel = styled.div`
  order: 1;
  padding-bottom: 0.9375rem;
  width: 100%;

  ${media.w.min.px768(css`
    order: 2;
    padding-bottom: 2.5rem;
    width: 32%;
  `)};

  ${media.w.min.px992(css`
    padding-bottom: 2.1875rem;
    width: 40%;
  `)};

  ${media.w.min.px1200(css`
    padding-bottom: 4.0625rem;
    width: 100%;
  `)};
`;

const MobileCourseEventSelectPanel = styled(CourseEventSelectPanel)`
  background: ${colors.white};
  border-radius: 0.25rem;
  bottom: 0;
  box-shadow: ${shadows.drop1};
  left: 50%;
  padding: 0.125rem 1rem 0.6875rem;
  position: fixed;
  transform: translate3d(-50%, 0, 0);
  width: 21rem;
  z-index: ${layers.widget};
`;

const DesktopCourseEventSelectPanel = styled(CourseEventSelectPanel)`
  background: ${colors.white};
  border-radius: 0.25rem;
  box-shadow: ${shadows.drop1};
  left: 50%;
  padding: 0.125rem 1rem 0.6875rem;
  z-index: ${layers.widget};

  margin-bottom: 1.4375rem;
  margin-top: -2.25rem;
  position: static;

  ${media.w.min.px992(css`
    margin-top: -1rem;
  `)};

  ${media.w.min.px1200(css`
    margin-top: -2.375rem;
  `)};
`;

const Section = styled.div`
  margin-bottom: 2.5rem;

  p {
    margin: 0;
  }
`;

const SectionCertificate = styled(Section)`
  align-items: flex-start;
  display: flex;
`;

export interface CoursePageProps extends RouteComponentProps {
  ssrCourseDTO?: CourseDTO;
  ssrCourseEventsDTO?: Array<EventDTO> | Array<EventWithAvailabilityStatusDTO>;
}

export const CoursePage: FC<CoursePageProps> = ({ ssrCourseDTO, ssrCourseEventsDTO }) => {
  const dispatch = useDispatch();
  const navigate = useGatsbyLikeNavigate();
  const router = useRouter();

  const courseIdParam = pageParam(router.query.courseId);
  const eventIdParam = pageParam(router.query.eventId);

  const courseId = courseIdParam ? coerceInt(courseIdParam) : undefined;
  const eventId = eventIdParam ? coerceInt(eventIdParam) : undefined;

  const postRedirectRegister = useRegisterAfterRedirect(courseId, eventId); // has to be before useResetRedirectUrl
  useResetRedirectUrl();

  const { isLoading: isCourseLoading, data: course } = useCourseQuery(
    courseId,
    () => ssrCourseDTO && mapToCourse(ssrCourseDTO),
  );
  const { isLoading: isCourseEventsLoading, data: courseEvents } = useCourseEventsQuery(
    courseId,
    () => ssrCourseEventsDTO && ssrCourseEventsDTO.map(mapToCourseEvent),
  );

  const selectedCourseEvent = courseEvents?.find((event) => event.id === eventId);

  const isPageLoading =
    (!course && isCourseLoading) ||
    (!courseEvents && isCourseEventsLoading) ||
    postRedirectRegister.isLoading;

  const navigateByEvent = useCallback(
    (event: CourseEvent) => {
      if (!course) {
        return;
      }

      navigate(
        getCourseEventSlugRoute(event, course),
        {
          replace: true,
        },
        {
          scroll: false,
          shallow: true,
        },
      );
    },
    [course, navigate],
  );

  const handleEventSelect = useCallback(
    (event: CourseEvent) => navigateByEvent(event),
    [navigateByEvent],
  );

  useEffect(() => {
    if (course) {
      const primaryCategory =
        course.categories.find((category) => category.main) ?? course.categories?.[0];

      primaryCategory && dispatch(layoutActions.setActivePanelCategory(String(primaryCategory.id)));
    }
  }, [course]);

  const seoKeywords = useMemo(() => prepareKeywordsString(course?.keywords), [course]);

  return (
    <Layout
      leftPanel={<LayoutPanelCategories />}
      seoDescription={course?.description}
      seoKeywords={seoKeywords}
      title={course?.title}
      widgetPadding={116}
      scrollToTopButton
    >
      {isPageLoading && <SimpleContentLoader />}
      {course && (
        <>
          <LayoutBox mobileBackground tabletBackground>
            <CourseHeader course={course} selectedCourseEvent={selectedCourseEvent} />

            <Container>
              <Content>
                {!isNilEmpty(selectedCourseEvent?.remarks) && (
                  <Section>
                    <SectionTitle>Ważne informacje</SectionTitle>
                    {selectedCourseEvent?.remarks}
                  </Section>
                )}

                <Section>
                  <SectionTitle>Czego się dowiesz i&nbsp;nauczysz?</SectionTitle>

                  <CourseParameterList items={course.learningPoints} />
                </Section>

                <Section>
                  <SectionTitle>Do kogo skierowane jest szkolenie?</SectionTitle>
                  <CourseParameterList items={course.targetGroups} />
                </Section>

                <Section>
                  <SectionTitle>Agenda szkolenia</SectionTitle>

                  {selectedCourseEvent && (
                    <StyledCourseEventAgendaList courseEvent={selectedCourseEvent} />
                  )}
                </Section>

                {!isNilEmpty(course.comment) && (
                  <Section>
                    <SectionTitle>Słowo od prowadzącego</SectionTitle>
                    {course.comment}
                  </Section>
                )}

                <SectionCertificate>
                  <CertificateImage>
                    <Image src={courseSampleCertificate} />
                  </CertificateImage>

                  <div>
                    <SectionTitle>Certyfikacja</SectionTitle>

                    <p>Uczestnicy szkolenia otrzymują certyfikat udziału w&nbsp;szkoleniu.</p>
                  </div>
                </SectionCertificate>
              </Content>

              <RightPanel>
                <MediaQuery minWidth={widthBreakpoints.px768}>
                  <>
                    {courseEvents && selectedCourseEvent && (
                      <DesktopCourseEventSelectPanel
                        course={course}
                        courseEvents={courseEvents}
                        selectedCourseEvent={selectedCourseEvent}
                        onChange={handleEventSelect}
                      />
                    )}
                  </>
                </MediaQuery>

                <CourseSpeaker course={course} />
              </RightPanel>
            </Container>

            <CourseFooter />
          </LayoutBox>

          <CourseRelatedCourses courseId={courseId} />

          <MediaQuery maxWidth={below(widthBreakpoints.px768)}>
            <>
              {courseEvents && selectedCourseEvent && (
                <MobileCourseEventSelectPanel
                  course={course}
                  courseEvents={courseEvents}
                  selectedCourseEvent={selectedCourseEvent}
                  onChange={handleEventSelect}
                />
              )}
            </>
          </MediaQuery>
        </>
      )}
    </Layout>
  );
};

export const getServerSideProps = VendorGetServerSideProps<CoursePageProps>(async (ctx) => {
  try {
    const {
      courseId: courseIdParam,
      eventId: eventIdParam,
      slug: slugParam,
    } = assertCourseWithEventParams(ctx.params);

    const slugExists = isNaN(Number(slugParam));

    const courseId = slugExists ? coerceInt(courseIdParam) : coerceInt(slugParam);
    const eventId = slugExists ? coerceInt(eventIdParam) : coerceInt(courseIdParam);

    const courseDTO = await getCourseDTOById(courseId);

    // jeżeli pierwszy parametr jest liczbą (nie jest slugiem), tzn że do wyświetlenia jest tutorial, success, survey albo congratulations
    // nie sprawdzamy czy są jakieś otwarte wydarzenia, bo takich może nie być. Dostęp do np ankiety powinien być możliwy bez tego. Resztę warunków ograniają konkretne podstrony po przekierowaniu.
    // zostaje więc pobranie szkolenia (dla sluga) i przekierowanie na docelową podstronę
    if (!slugExists) {
      return {
        redirect: {
          destination: buildRedirectUrl(
            `${getCourseEventSlugRoute({ id: eventId }, courseDTO)}/${
              courseSegmentsTranslations[eventIdParam] ?? eventIdParam
            }`,
            ctx,
          ),
          statusCode: permanentRedirectCode,
        },
      };
    }

    const courseEvents = await getEventsByCourseId(courseId, false);

    const selectedEvent = autoselectEvent(courseEvents, eventId);

    // jeżeli szkolenie nie ma żadnych dostępnych eventów (status OPEN albo ONGOING)
    // przekieruj na homepage
    if (!selectedEvent) {
      return {
        redirect: {
          destination: buildRedirectUrl(HomepageRoute, ctx),
          statusCode: temporaryRedirectCode,
        },
      };
    }

    // jeżeli przesłane w urlu id wydarzenia nie istnieje
    // przekieruj na nowe, wybrane automatycznie
    if (selectedEvent.id !== eventId) {
      return {
        redirect: {
          destination: buildRedirectUrl(getCourseEventSlugRoute(selectedEvent, courseDTO), ctx),
          statusCode: temporaryRedirectCode,
        },
      };
    }

    const { key } = buildUnauthenticatedUserKeyEnhancer([QueryKey.RelatedCourses, courseId]);
    await ctx.queryClient.prefetchQuery(key, () => getRelatedCourses(courseId, false));

    return {
      props: {
        ssrCourseDTO: courseDTO,
      },
    };
  } catch (error) {
    if (isErrorResponse(error) && error.status === StatusCodes.NOT_FOUND) {
      // jeżeli dostaliśmy z api 404, zwróć 404
      return {
        notFound: true,
      };
    }
  }

  // w przypadku każdego innego błędu poza 404, przekieruj na homepage
  return {
    redirect: {
      destination: buildRedirectUrl(HomepageRoute, ctx),
      statusCode: temporaryRedirectCode,
    },
  };
});

export async function getSitemap(): Promise<SitemapEntry[]> {
  const events = await all(listEventsOnlineGenerator({}, false));

  return events.map((event) => {
    const sitemapEntry: SitemapEntry = {
      loc: getCourseEventSlugRoute(event, {
        slug: event.courseSlug,
        id: event.courseId,
      }),
    };

    if (event.courseImage) {
      sitemapEntry.image = {
        image: {
          loc: event.courseImage.url,
        },
      };
    }

    return sitemapEntry;
  });
}

export default CoursePage;
